/**
 * Utility for sanitizing HTML content to prevent XSS attacks
 */
import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content before rendering
 * @param {string} dirty - The unsanitized HTML string
 * @param {object} config - Optional DOMPurify configuration
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHtml = (dirty, config = {}) => {
  if (!dirty) return '';

  const defaultConfig = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'a', 'ul', 'ol', 'li',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'blockquote', 'code', 'pre',
      'img', 'video', 'iframe'
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    ...config
  };

  return DOMPurify.sanitize(dirty, defaultConfig);
};

/**
 * Sanitize HTML for display in React using dangerouslySetInnerHTML
 * @param {string} html - The HTML string to sanitize
 * @returns {object} - Object with __html property safe for React
 */
export const createSafeMarkup = (html) => {
  return {
    __html: sanitizeHtml(html)
  };
};

/**
 * Strip all HTML tags from content
 * @param {string} html - The HTML string
 * @returns {string} - Plain text without HTML
 */
export const stripHtml = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
};
